import { FileImage, FileText, Link2, ShieldAlert, Terminal } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import PdfPages from '@/components/PdfPages';
import { useExportHold, useExporting } from '@/components/report/exporting';
import type { PreviewKind } from '@/lib/preview/dispatch';
import { buildPreviewDocument, readPreviewTheme } from '@/lib/preview/render';
import { failureMessage, useConverter } from '@/lib/preview/useConverter';
import { renderFailureMessage, usePdfPages } from '@/lib/preview/usePdfPages';

/** What a document says, shown as its intended reader would see it.
 *
 *  One renderer, two callers: before a run it is how an analyst decides what to
 *  detonate, and on the report it is what the sample was pretending to be. It
 *  takes a decided kind and the bytes rather than reaching for either - the
 *  submit page has a staged entry, the report an analysed container, and neither
 *  vocabulary belongs here.
 *
 *  No score and no verdict in either place. Callers key it on whatever
 *  identifies the document, so a different one remounts with clean state. */

interface Props {
  /** How to render, decided by the caller from what it has. */
  kind: PreviewKind;
  /** The document's name, shown in the header. */
  name: string;
  /** The document's bytes, or null while they are unavailable. */
  bytes: Uint8Array | null;
  /** What to say when there are no bytes - the reason differs per caller. */
  unavailable: ReactNode;
}

/** How much of a text file to show. */
const TEXT_PREVIEW_LIMIT = 256 * 1024;

/** Width to rasterise at: legible body text without a print-resolution render. */
const PDF_RENDER_WIDTH = 900;

export default function PreviewPane({ kind, name, bytes, unavailable }: Props) {
  const { state, convert } = useConverter();
  const { state: pdfState, render: renderPdf } = usePdfPages();
  /** The converter is a large download, fetched only when asked for. */
  const [converterRequested, setConverterRequested] = useState(false);

  // Rasterising a PDF is not a query, so nothing in the export's readiness
  // answer would wait for it: the pages would arrive after the print engine had
  // already taken its snapshot of "Rendering pages…". This is the one place in
  // the report that needs a hold of its own.
  useExportHold(kind === 'pdf' && (pdfState.status === 'idle' || pdfState.status === 'rendering'));

  // A PDF renders to page images rather than converting to text, so it takes
  // its own path and does not wait on the converter's consent gate.
  useEffect(() => {
    if (!bytes || kind !== 'pdf') return;
    renderPdf(bytes.slice(), PDF_RENDER_WIDTH);
  }, [bytes, kind, renderPdf]);

  useEffect(() => {
    if (!bytes || kind !== 'document' || !converterRequested) return;
    // The worker takes ownership, so hand it a copy: the caller still needs
    // these bytes to upload.
    convert(bytes.slice());
  }, [bytes, kind, converterRequested, convert]);

  return (
    <section className="rounded-lg border border-ink-800 bg-ink-900" aria-label="Sample preview">
      <header className="flex items-center gap-2 border-b border-ink-800 px-4 py-2.5">
        <PreviewIcon kind={kind} />
        <h3 className="text-prose font-medium text-ink-100">Preview</h3>
        <span className="ml-auto font-mono text-mini text-ink-400 wrap-anywhere">{name}</span>
      </header>
      <div className="px-4 py-3">
        <PreviewBody
          bytes={bytes}
          kind={kind}
          unavailable={unavailable}
          state={state}
          pdfState={pdfState}
          requested={converterRequested}
          onRequest={() => setConverterRequested(true)}
        />
      </div>
      <footer className="border-t border-ink-800 px-4 py-2 text-mini text-ink-500">
        Pages are rendered to images and text is shown as written by the sample. Nothing here
        is clickable and nothing loads.
      </footer>
    </section>
  );
}

function PreviewIcon({ kind }: { kind: PreviewKind }) {
  const cls = 'h-4 w-4 text-ink-400';
  if (kind === 'text') return <Terminal className={cls} aria-hidden />;
  if (kind === 'shortcut') return <Link2 className={cls} aria-hidden />;
  if (kind === 'pdf') return <FileImage className={cls} aria-hidden />;
  return <FileText className={cls} aria-hidden />;
}

interface BodyProps {
  bytes: Uint8Array | null;
  kind: PreviewKind;
  unavailable: ReactNode;
  state: ReturnType<typeof useConverter>['state'];
  pdfState: ReturnType<typeof usePdfPages>['state'];
  requested: boolean;
  onRequest: () => void;
}

function PreviewBody({
  bytes,
  kind,
  unavailable,
  state,
  pdfState,
  requested,
  onRequest,
}: BodyProps) {
  if (!bytes) return <Note>{unavailable}</Note>;

  switch (kind) {
    case 'pdf':
      return <PdfPreview state={pdfState} />;
    case 'document':
      return <DocumentPreview state={state} requested={requested} onRequest={onRequest} />;
    case 'text':
      return <TextPreview bytes={bytes} />;
    case 'shortcut':
      return (
        <Note>
          A Windows shortcut. Its target and arguments are what actually runs; the server
          resolves them during inspection.
        </Note>
      );
    case 'binary':
      return (
        <Note>
          A Windows binary has no readable content to preview. Its structure - sections,
          imports, entropy - is profiled after submission.
        </Note>
      );
    case 'container':
      return <Note>An archive. The file tree beside this pane is its contents.</Note>;
    default:
      return <Note>Nothing to preview for this format.</Note>;
  }
}

function PdfPreview({ state }: { state: ReturnType<typeof usePdfPages>['state'] }) {
  if (state.status === 'idle' || state.status === 'rendering') {
    return <Note>Rendering pages…</Note>;
  }
  if (state.status === 'failed') {
    return (
      <div className="flex items-start gap-2">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-verdict-suspicious" aria-hidden />
        <p className="text-prose text-ink-300">{renderFailureMessage(state.reason)}</p>
      </div>
    );
  }
  if (state.pages.length === 0) {
    return <Note>The document has no pages to render.</Note>;
  }
  return <PdfPages pages={state.pages} totalPages={state.totalPages} />;
}

interface DocumentProps {
  state: ReturnType<typeof useConverter>['state'];
  requested: boolean;
  onRequest: () => void;
}

function DocumentPreview({ state, requested, onRequest }: DocumentProps) {
  const theme = useMemo(() => readPreviewTheme(), []);
  const exporting = useExporting();

  if (!requested) {
    // The converter is a 2.7 MB download behind a deliberate consent gate, and
    // an export must not spend it on the reader's behalf. Say what is missing
    // and where to get it rather than printing a dead button.
    if (exporting) {
      return (
        <Note>
          The document's rendered contents are not reproduced here. Open the report in the
          dashboard and choose "Read this document" - rendering it loads a converter on demand.
        </Note>
      );
    }
    return (
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onRequest}
          className="rounded border border-ink-700 bg-ink-850 px-3 py-1.5 text-prose text-ink-100 hover:bg-ink-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-400"
        >
          Read this document
        </button>
        <span className="text-mini text-ink-500">Loads a 2.7 MB converter, once per session.</span>
      </div>
    );
  }
  if (state.status === 'idle' || state.status === 'converting') {
    return <Note>Reading the document…</Note>;
  }
  if (state.status === 'failed') {
    return (
      <div className="flex items-start gap-2">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-verdict-suspicious" aria-hidden />
        <p className="text-prose text-ink-300">{failureMessage(state.reason)}</p>
      </div>
    );
  }
  if (state.status !== 'done') return null;

  return (
    <iframe
      // Empty sandbox: no scripts, popups, navigation or same-origin access.
      // With the document's own default-src 'none', a renderer mistake still
      // cannot make a request.
      sandbox=""
      srcDoc={buildPreviewDocument(state.markdown, theme)}
      title="Document content"
      className="h-96 w-full rounded border border-ink-800 bg-ink-950"
    />
  );
}

function TextPreview({ bytes }: { bytes: Uint8Array }) {
  const [text, truncated] = useMemo(() => {
    const slice = bytes.subarray(0, TEXT_PREVIEW_LIMIT);
    // Invalid sequences become U+FFFD rather than throwing: a deliberately
    // mangled script is still worth reading.
    const decoded = new TextDecoder('utf-8', { fatal: false }).decode(slice);
    return [decoded, bytes.length > TEXT_PREVIEW_LIMIT] as const;
  }, [bytes]);

  return (
    <>
      <pre className="max-h-96 overflow-auto rounded border border-ink-800 bg-ink-950 p-3 font-mono text-mini leading-relaxed text-ink-200">
        {text}
      </pre>
      {truncated && (
        <p className="mt-2 text-mini text-ink-500">
          Showing the first {Math.round(TEXT_PREVIEW_LIMIT / 1024)} KB.
        </p>
      )}
    </>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return <p className="text-prose text-ink-400">{children}</p>;
}
